import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Navbar from './components/Navbar';
import Footer from './components/Footer';

import Dashboard from './components/pages/Dashboard';
import TelegramAccounts from './components/pages/TelegramAccountManagement';
import ProxyManagement from './components/pages/ProxyManagement';
import MessageCreation from './components/pages/MessageCreation';
import TaskScheduler from './components/pages/TaskScheduler';
import ArchiveFiltering from './components/pages/ArchiveFiltering';
import UserManagement from './components/pages/UserManagement';

import { Box } from '@mui/material';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import LoginPage from './components/pages/Login';
import NoPermission from './components/pages/NoPermission';
import MessagesPage from './components/pages/MessagesManagement';
import CampaignManager from './components/pages/Campaigns';
import ApiService from './API/Requesthandler';
import ScrapingTasks from './components/pages/ScrapingTasks';
import MessagesLogger from './components/pages/MessagesLogger';
import MembersScraper from './components/pages/MembersScraper';

const drawerWidth = 240; // Sidebar width

export default function App() {
  const [userData, setUserData] = useState(() => {
    try {
      const storedUserData = localStorage.getItem('user-data');
      // console.log("Stored User Data:", storedUserData);
      
      if (!storedUserData) return null; // No data found
      
      const parsedData = JSON.parse(storedUserData);
      // console.log("Parsed User Data:", parsedData);
      
      return parsedData;
    } catch (error) {
      console.error("Error parsing data:", error);
      return null;
    }
  });

  const [permissionsData, setPermissionsData] = useState([]);
  const [mode, setMode] = useState(window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  const [isLoading, setIsLoading] = useState(true); // Prevent rendering before permissions are fetched

  useEffect(() => {
    
    if (userData != null) {
      ApiService.get('/api/users/get_permission?id=' + userData.id)
        .then(res => {
          setPermissionsData(res.data);
        })
        .catch(err => {
          console.log(err)
          localStorage.clear()
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [userData]);

  const checkPermission = (permission) => permissionsData.includes(permission);

  const theme = createTheme({
    palette: {
      mode,
      primary: { main: '#0B7189' },
      secondary: { main: '#1de9b6' },
    },
    typography: { fontFamily: 'Inter, sans-serif' },
  });





  const toggleTheme = () => setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {!isLoading && (
          <MainContent 
            userData={userData} 
            checkPermission={checkPermission} 
            toggleTheme={toggleTheme} 
            setUserData={setUserData} 
          />
        )}
      </Router>
    </ThemeProvider>
  );
}

function MainContent({ userData, checkPermission, toggleTheme, setUserData }) {
  const location = useLocation();

  // Redirect to login if not logged in
  if (!userData && location.pathname !== '/') {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ display: 'flex', width: "100%" }}>
      {userData && location.pathname !== '/' && (
        <>
          <Sidebar />
          <TopBar activeAccount={userData} toggleTheme={toggleTheme} />
        </>
      )}

      <Box component="main" sx={{ flexGrow: 1, width: `calc(100% - ${drawerWidth}px)`, marginTop: '80px' }}>
        <Routes>
          {!userData ? (
            <Route path="*" element={<LoginPage />} />
          ) : (
            <>
              <Route path="/dashboard" element={checkPermission("view_reports") ? <Dashboard /> : <NoPermission />} />
              <Route path="/user-management" element={checkPermission("manage_users") ? <UserManagement /> : <NoPermission />} />
              <Route path="/telegram-accounts" element={checkPermission("telegram_accounts") ? <TelegramAccounts /> : <NoPermission />} />
              <Route path="/proxy-management" element={checkPermission("manage_proxy") ? <ProxyManagement /> : <NoPermission />} />
              <Route path="/message-creation" element={checkPermission("send_messages") ? <MessageCreation /> : <NoPermission />} />
              <Route path="/task-scheduler" element={checkPermission("schedule_posts") ? <TaskScheduler /> : <NoPermission />} />
              <Route path="/archive-filtering" element={checkPermission("archive_content") ? <ArchiveFiltering /> : <NoPermission />} />
              <Route path="/message-management" element={checkPermission("manage_messages") ? <MessagesPage /> : <NoPermission />} />
              <Route path="/campaigns" element={checkPermission("campaigns") ? <CampaignManager /> : <NoPermission />} />
              <Route path="/scraper" element={checkPermission("scraper") ? <ScrapingTasks /> : <NoPermission />} />
              <Route path="/logger" element={checkPermission("logger") ? <MessagesLogger /> : <NoPermission />} />
              <Route path="/members_scraper" element={checkPermission("members_scraper") ? <MembersScraper /> : <NoPermission />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

            </>
          )}
        </Routes>
        <Footer />
      </Box>
    </Box>
  );
}
